import React, { useState, useEffect } from 'react';
import { HERO_IMAGE, LOGO_HORIZONTAL } from '../data/images';

// ── HERO CONTENT ──────────────────────────────────────────────
// Edit headline, subheadline, and button text here
const HERO_CONTENT = {
  // UPDATE: main headline — change to suit your semester/theme
  headline: 'Empowering the Next Generation of Hispanic Engineers at Virginia Tech',
  // UPDATE: subheadline
  subheadline: 'A student-led community dedicated to professional excellence, academic growth, and making a lasting impact in STEM.',
  // Buttons — update labels and hrefs
  // Join SHPE links to SHPE National's membership page (not MEMBERSHIP.joinUrl /
  // HokieSync, which isn't working yet). UPDATE back once that link is fixed.
  primaryBtn: { label: 'Join SHPE', href: 'https://shpe.org/membership', external: true },
  // Upcoming Events scrolls to the calendar section — its real id is
  // #calendar (see CalendarSection.js), not #events.
  secondaryBtn: { label: 'Upcoming Events', href: '#calendar', external: false },
};

// ── STATS ─────────────────────────────────────────────────────
// UPDATE each semester
const STATS = [
  { value: '120+', label: 'Active Members' },
  { value: '25+',  label: 'Majors Represented' },
  { value: '8',    label: 'Corporate Partners' },
  { value: '2014', label: 'Founded' },
];

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <section id="home" style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      background: '#0D0D0D',
    }}>

      {/* HERO BACKGROUND IMAGE — update HERO_IMAGE in src/data/images.js */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${HERO_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        filter: 'brightness(0.4)',
        transition: 'opacity 1s ease',
        opacity: loaded ? 1 : 0,
      }} />

      {/* Gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, rgba(134,31,65,0.85) 0%, rgba(0,0,0,0.6) 60%, rgba(229,117,31,0.3) 100%)',
      }} />

      {/* Decorative circles */}
      <div style={{
        position: 'absolute', width: 600, height: 600,
        borderRadius: '50%', border: '1px solid rgba(229,117,31,0.15)',
        top: -100, right: -100, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: 400, height: 400,
        borderRadius: '50%', border: '1px solid rgba(255,255,255,0.06)',
        bottom: 50, left: -80, pointerEvents: 'none',
      }} />

      {/* HERO CONTENT */}
      <div style={{
        position: 'relative', zIndex: 1,
        maxWidth: 1200, margin: '0 auto', padding: '120px 24px 80px',
        width: '100%',
      }}>
        <div style={{ maxWidth: 700, opacity: loaded ? 1 : 0, transition: 'opacity 0.8s ease 0.3s' }}>

          {/* Top label */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: 'rgba(229,117,31,0.2)',
            border: '1px solid rgba(229,117,31,0.4)',
            borderRadius: 100, padding: '6px 16px',
            marginBottom: 24,
            animation: loaded ? 'fadeInUp 0.6s ease 0.2s both' : 'none',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E5751F', display: 'block', animation: 'pulse-dot 2s infinite' }} />
            <span style={{ color: '#F59142', fontSize: 13, fontWeight: 600, letterSpacing: 1 }}>SHPE @ Virginia Tech</span>
          </div>

          {/* HEADLINE — update HERO_CONTENT.headline above */}
          <h1 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
            fontWeight: 900, color: 'white', lineHeight: 1.12,
            marginBottom: 24,
            animation: loaded ? 'fadeInUp 0.7s ease 0.3s both' : 'none',
          }}>
            {HERO_CONTENT.headline}
          </h1>

          {/* SUBHEADLINE */}
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.2rem)',
            color: 'rgba(255,255,255,0.82)',
            lineHeight: 1.7, marginBottom: 40,
            animation: loaded ? 'fadeInUp 0.7s ease 0.4s both' : 'none',
          }}>
            {HERO_CONTENT.subheadline}
          </p>

          {/* CTA BUTTONS */}
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap',
            animation: loaded ? 'fadeInUp 0.7s ease 0.5s both' : 'none',
          }}>
            <a
              href={HERO_CONTENT.primaryBtn.href}
              target={HERO_CONTENT.primaryBtn.external ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ fontSize: 15, padding: '15px 32px' }}>
              {HERO_CONTENT.primaryBtn.label} →
            </a>
            <a
              href={HERO_CONTENT.secondaryBtn.href}
              className="btn btn-secondary"
              style={{ fontSize: 15, padding: '15px 32px' }}>
              {HERO_CONTENT.secondaryBtn.label}
            </a>
          </div>
        </div>

        {/* STATS ROW */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 2, marginTop: 80,
          maxWidth: 600,
          animation: loaded ? 'fadeInUp 0.7s ease 0.7s both' : 'none',
        }}>
          {STATS.map((stat, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '20px 16px',
              borderRight: i < STATS.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
            }}>
              <div style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                fontWeight: 900, color: '#E5751F',
              }}>{stat.value}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 4, letterSpacing: 0.5 }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        animation: 'fadeIn 1s ease 1.5s both',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: 2 }}>SCROLL</span>
        <div style={{
          width: 1, height: 40, background: 'linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)',
        }} />
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.5); opacity: 0.7; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </section>
  );
}